import { useCallback, useMemo } from 'react';
import { useDebounce } from 'react-use';
import { Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { ListWithSearch } from '~components/atoms';
import { useGooglePlacesAutocompleteService } from '~components/atoms/custom/autocomplete/AutoCompleteGoogleMapsWithDetail/hooks/useGooglePlacesAutocompleteService';
import { useGooglePlacesService } from '~components/atoms/custom/autocomplete/AutoCompleteGoogleMapsWithDetail/hooks/useGooglePlacesService';
import { PlaceType } from '~components/atoms/custom/autocomplete/AutoCompleteGoogleMapsWithDetail/type';
import { useVenueExistenceStatusAssociateByGooglePlaceId } from '~hooks/useQuery';
import {
  PINNING_STEP,
  usePinningStepContext,
  useStartAndFindVenueContext,
} from '~providers';
import { IFormCreationValues } from '~providers/useForm';
import { FilterLocationOption } from '~types';
import { isCannotEditVenue } from '~utils/venue.util';

export function GooglePlacesSearchForVenue() {
  const { placeOptions, setSearchValue, isFetchingPlaces } =
    useGooglePlacesAutocompleteService();
  const { googlePlaceDetailOrUndefined } = useGooglePlacesService();
  const venueExistenceStatus = useVenueExistenceStatusAssociateByGooglePlaceId({
    request: {
      googlePlaceIds: placeOptions.map((option) => option.place_id),
    },
    options: {
      enabled: placeOptions.length > 0,
    },
  });

  const { setFieldValue, setFieldTouched } =
    useFormikContext<IFormCreationValues>();
  const {
    venueName,
    optionsOpen,
    handleSetInitialFocus,
    onInputValueChange,
    isVenueNotExisted,
    isTyping,
    handleSetIsTyping,
  } = useStartAndFindVenueContext();
  const {
    goToNextStep,
    handleSetSelectedVenueId,
    handleSetSelectedGooglePlaceId,
    handleSetPinningStep,
  } = usePinningStepContext();

  const handleGoToNextStep = useCallback(() => {
    handleSetSelectedVenueId(undefined);
    handleSetSelectedGooglePlaceId(undefined);
    setFieldValue('venue.locationName', venueName);
    goToNextStep();
  }, [
    goToNextStep,
    handleSetSelectedGooglePlaceId,
    handleSetSelectedVenueId,
    setFieldValue,
    venueName,
  ]);

  const setAddress = useCallback(
    (value: FilterLocationOption) => {
      setFieldValue('venue.address.id', value.id, false);
      setFieldValue('venue.address.name', value.name, false);
      setFieldValue('venue.address.location.lat', value.location.lat, false);
      setFieldValue('venue.address.location.lng', value.location.lng, false);
      setFieldValue(
        'venue.address.location.infoAddress.country',
        value.location.infoAddress.country,
        false,
      );
      setFieldValue(
        'venue.address.location.infoAddress.countryCode',
        value.location.infoAddress.countryCode,
        false,
      );
      setFieldValue(
        'venue.address.location.infoAddress.city',
        value.location.infoAddress.city,
        false,
      );
      setFieldValue(
        'venue.address.location.infoAddress.state',
        value.location.infoAddress.state,
        false,
      );
      setFieldValue(
        'venue.address.location.infoAddress.zip',
        value.location.infoAddress.zip,
        false,
      );
      // Use setFieldTouched to display the error message
      // when the user is on the step PINNING_STEP.CREATE_VENUE_ADD_ADDRESS.
      setFieldTouched('venue.address', true, false);
    },
    [setFieldTouched, setFieldValue],
  );

  // Filter out all the place that can't be updated or can't be created
  const filteredOptionsByVenueExistenceStatus = useMemo(() => {
    return placeOptions.filter((option) => {
      const venue = venueExistenceStatus.data?.get(option.place_id);

      return venue && !isCannotEditVenue(venue);
    });
  }, [placeOptions, venueExistenceStatus.data]);

  const onGooglePlaceOptionClick = useCallback(
    (googlePlace: PlaceType) => async () => {
      const venue = venueExistenceStatus.data?.get(googlePlace.place_id);
      // Create WITHOUT Google Place ID flow
      if (!venue) {
        handleGoToNextStep();
        return;
      }
      // Edit flow
      if (venue.isExisting) {
        handleSetSelectedVenueId(venue.venueId);
        handleSetPinningStep(PINNING_STEP.CONFIRM_VENUE);
        return;
      }
      // Create with Google Place
      const googlePlaceDetail = await googlePlaceDetailOrUndefined(googlePlace);
      if (googlePlaceDetail) {
        const location = googlePlaceDetail.location;
        const infoAddress = location.infoAddress;
        const isInvalidAddress =
          !googlePlaceDetail.name ||
          !location.lat ||
          !location.lng ||
          !infoAddress.city ||
          !infoAddress.state ||
          !infoAddress.zip ||
          !infoAddress.country ||
          !infoAddress.countryCode;

        if (isInvalidAddress) {
          handleSetSelectedGooglePlaceId(undefined);
        } else {
          handleSetSelectedGooglePlaceId(googlePlaceDetail.id);
        }
        setAddress(googlePlaceDetail);
        setFieldValue(
          'venue.locationName',
          googlePlace.structured_formatting.main_text,
          true,
        );
        setFieldValue('venue.operatingHours', location.operatingHours, true);
        goToNextStep();
      } else {
        handleGoToNextStep();
      }
    },
    [
      goToNextStep,
      googlePlaceDetailOrUndefined,
      handleGoToNextStep,
      handleSetPinningStep,
      handleSetSelectedGooglePlaceId,
      handleSetSelectedVenueId,
      setAddress,
      setFieldValue,
      venueExistenceStatus.data,
    ],
  );

  const shouldShowNotFoundMessage =
    !!venueName &&
    !isFetchingPlaces &&
    !venueExistenceStatus.isFetching &&
    isVenueNotExisted(filteredOptionsByVenueExistenceStatus.length);

  // Handle showing not found message
  useDebounce(
    () => {
      if (isTyping) {
        handleSetIsTyping(false);
      }
    },
    // This value should be larger than the Google Places search debounce time.
    // Before the debounce time elapses, isTyping will already be true,
    // but the latest Google Places data may not have been fetched yet,
    // This will cause the message to always be displayed because the old data.length > 0
    450,
    [venueName, isTyping, handleSetIsTyping],
  );

  return (
    <>
      <ListWithSearch
        debounceTime={0}
        onSearching={setSearchValue}
        optionsOpen={optionsOpen}
        onInputValueChange={onInputValueChange}
        textFieldProps={{ onFocus: handleSetInitialFocus }}
      >
        {filteredOptionsByVenueExistenceStatus.map((googlePlace) => (
          <Stack
            paddingY={1.5}
            key={googlePlace.place_id}
            onClick={onGooglePlaceOptionClick(googlePlace)}
          >
            <Typography color="text.dark" fontSize="16px">
              {googlePlace.structured_formatting.main_text}
            </Typography>
            <Typography color="text.placeholder" fontSize="14px">
              {googlePlace.structured_formatting.secondary_text}
            </Typography>
          </Stack>
        ))}
      </ListWithSearch>
      {shouldShowNotFoundMessage && (
        <Stack marginTop={-2}>
          <Typography variant="body2" color="#6B7280">
            Unable to find “{venueName}”. Try a different search term.
          </Typography>
        </Stack>
      )}
    </>
  );
}
